import type { EmployeeStatus, EmploymentType } from '@/features/hrm/employees/constants';
import type { LeaveType } from '@/features/hrm/time-off';

/** Mirrors CabinetProfileReadSerializer (apps/cabinet/serializers.py). */
export interface CabinetProfile {
  public_id: string;
  /** Photo URL served by the backend; null if unset. */
  photo: string | null;
  first_name: string;
  last_name: string;
  /** Read-only — changed via account admin, never here. */
  email: string;
  middle_name: string | null;
  personal_email: string;
  phone_number: string | null;
  address: string | null;
  city: string;
  birth_date: string | null;
  job_title: string;
  department_name: string | null;
  manager_name: string | null;
  employment_type: EmploymentType;
  hire_date: string | null;
  trial_period_end: string | null;
  end_date: string | null;
  status: EmployeeStatus;
  country: string;
  /** Your default sales commission rate (%), set by an admin. `null` if none. */
  default_commission_pct: string | null;
  created: string;
  modified: string;
}

/**
 * Mirrors CabinetSalaryRecordSerializer — the member's OWN compensation,
 * deliberately unmasked (amounts are always present, unlike the HR view).
 */
export interface CabinetSalaryRecord {
  id: number;
  public_id: string;
  effective_date: string;
  note: string;
  amount: string | null;
  currency: string | null;
  fx_rate_to_base: string | null;
  base_amount: string | null;
  created: string;
  modified: string;
}

/** Mirrors CabinetPayslipSerializer — the member's OWN payslips (salary actually
 * paid each period), deliberately unmasked. */
export interface CabinetPayslip {
  id: number;
  public_id: string;
  period_start: string | null;
  period_end: string | null;
  currency: string | null;
  fx_rate_to_base: string | null;
  gross: string | null;
  deductions: string | null;
  employer_cost: string | null;
  net: string | null;
  base_net: string | null;
}

/** Self-filed leave request payload (CabinetTimeOffWriteSerializer). */
export interface CabinetTimeOffInput {
  type: LeaveType;
  start_date: string;
  end_date: string;
  days: string;
  reason?: string;
}

/** Mirrors CabinetEmployeeSkillReadSerializer — own skill with resolved name. */
export interface CabinetEmployeeSkill {
  id: number;
  skill: number;
  skill_name: string | null;
  skill_category: string | null;
  level: number;
  created: string;
  modified: string;
}

export interface CabinetSkillInput {
  skill: number;
  level: number;
}

export type CabinetDocumentType =
  | 'agreement'
  | 'sow'
  | 'nda'
  | 'offer'
  | 'proposal'
  | 'cv'
  | 'email'
  | 'project_materials'
  | 'other';

export type CabinetDocumentStatus =
  | 'draft'
  | 'rendered'
  | 'pending_signature'
  | 'signed'
  | 'void';

/** Subset of DocumentReadSerializer the cabinet renders (apps/documents). */
export interface CabinetDocument {
  id: number;
  name: string;
  type: CabinetDocumentType;
  status: CabinetDocumentStatus;
  /** File URL; null when none attached. */
  file: string | null;
  signed_at: string | null;
  created: string;
  modified: string;
}

export const cabinetName = (
  p: Pick<CabinetProfile, 'first_name' | 'last_name' | 'email'>,
): string => `${p.first_name} ${p.last_name}`.trim() || p.email || 'Me';
